class TpsWorkShutter extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 1;
    this.selectedOption = null;
    this.inputs = {};
    this.liveRegion = null;
    this.errorText = 'Enter a number greater than 0.';
    this.continueReadyText = 'Continue is available.';
    this.reduceMotion = false;
    this.init();
  }

  init() {
    this.liveRegion = this.querySelector('[data-shutter-live]');
    this.errorText = this.dataset.errorText || this.errorText;
    this.continueReadyText = this.dataset.continueReadyText || this.continueReadyText;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.bindEvents();
    this.setupRealtimeValidation();
  }

  bindEvents() {
    const options = this.querySelectorAll('.tps-shutter-option-select');
    options.forEach(option => {
      option.addEventListener('click', () => {
        const value = option.dataset.value;
        this.handleOptionSelect(value);
      });
    });

    const nextBtns = this.querySelectorAll('.next-step');
    nextBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const stepEl = e.target.closest('.tps-shutter-step');
        const stepNum = parseInt(stepEl.dataset.step);
        this.handleNextStep(stepNum);
      });
    });

    const prevBtns = this.querySelectorAll('.tps-shutter-prev-btn');
    prevBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        this.goToStep(this.currentStep - 1);
      });
    });

    const restartBtn = this.querySelector('.restart-btn');
    if (restartBtn) {
      restartBtn.addEventListener('click', () => {
        this.reset();
      });
    }
  }

  setupRealtimeValidation() {
    const steps = this.querySelectorAll('.tps-shutter-step');

    steps.forEach(step => {
      const allInputs = step.querySelectorAll('.tps-shutter-inputs .tps-input');
      const button = step.querySelector('.tps-shutter-btn.next-step');
      if (!allInputs.length || !button) return;

      const getActiveInputs = () => step.querySelectorAll('.tps-shutter-option-group:not(.hidden) .tps-shutter-inputs .tps-input');

      const validate = () => {
        const inputs = getActiveInputs();
        if (!inputs.length) {
          button.classList.add('hidden');
          return;
        }

        let allValid = true;
        const wasHidden = button.classList.contains('hidden');

        inputs.forEach(input => {
          const raw = input.value;
          const trimmed = raw.trim();
          const hasValue = trimmed !== '';
          const value = parseFloat(trimmed);
          const isNumber = !isNaN(value);
          const isPositive = isNumber && value > 0;

          if (!isPositive) {
            allValid = false;
          }

          this.setInputError(input, hasValue && (!isNumber || value <= 0));
        });

        if (allValid) {
          button.classList.remove('hidden');
          if (wasHidden) this.announce(this.continueReadyText);
        } else {
          button.classList.add('hidden');
        }
      };

      validate();

      allInputs.forEach(input => {
        input.addEventListener('input', validate);
        input.addEventListener('blur', () => {
          validate();
          const group = input.closest('.tps-shutter-input-group');
          if (group && group.classList.contains('tps-input-error')) {
            this.announce(this.errorText);
          }
        });
      });
    });
  }

  setInputError(input, isError) {
    const group = input.closest('.tps-shutter-input-group');
    const errorEl = group ? group.querySelector('[data-shutter-error]') : null;

    input.setAttribute('aria-invalid', isError ? 'true' : 'false');

    if (group) {
      group.classList.toggle('tps-input-error', isError);
    }

    if (errorEl) {
      errorEl.textContent = isError ? this.errorText : '';
    }
  }

  clearInputError(input) {
    this.setInputError(input, false);
  }

  handleOptionSelect(value) {
    this.selectedOption = value;
    this.syncOptionGroups(value);
    this.goToStep(2);
  }

  syncOptionGroups(optionValue) {
    const option = String(optionValue);

    this.querySelectorAll('.tps-shutter-option-group').forEach((group) => {
      const isMatch = group.dataset.forOption === option;
      group.classList.toggle('hidden', !isMatch);
      group.querySelectorAll('.tps-input').forEach((input) => {
        input.disabled = !isMatch;
        if (!isMatch) this.clearInputError(input);
      });
    });

    this.querySelectorAll('.tps-shutter-step').forEach((step) => {
      const button = step.querySelector('.tps-shutter-btn.next-step');
      if (!button) return;

      const inputs = step.querySelectorAll('.tps-shutter-option-group:not(.hidden) .tps-shutter-inputs .tps-input');
      if (!inputs.length) {
        button.classList.add('hidden');
        return;
      }

      const allValid = Array.from(inputs).every((input) => {
        const value = parseFloat(input.value.trim());
        return !isNaN(value) && value > 0;
      });

      button.classList.toggle('hidden', !allValid);
    });
  }

  handleNextStep(currentStep) {
    if (!this.validateStep(currentStep)) {
      return;
    }

    if (currentStep === 2) {
      this.goToStep(3);
    } else if (currentStep === 3) {
      this.goToStep(4);
    } else if (currentStep === 4) {
      this.showResult();
    }
  }

  validateStep(step) {
    const stepEl = this.querySelector(`.tps-shutter-step-${step}`);
    if (!stepEl) return false;

    const inputs = stepEl.querySelectorAll('.tps-shutter-option-group:not(.hidden) .tps-shutter-inputs .tps-input');
    let isValid = true;

    inputs.forEach(input => {
      const raw = input.value;
      const trimmed = raw.trim();
      const hasValue = trimmed !== '';
      const value = parseFloat(trimmed);
      const isNumber = !isNaN(value);
      const isPositive = isNumber && value > 0;

      if (!isPositive) {
        isValid = false;
      } else {
        this.inputs[input.name] = value;
      }

      this.setInputError(input, hasValue && (!isNumber || value <= 0));
    });

    return isValid;
  }

  goToStep(step, { announce = true } = {}) {
    const steps = this.querySelectorAll('.tps-shutter-step');
    const totalSteps = steps.length;

    steps.forEach(el => {
      const isActive = parseInt(el.dataset.step, 10) === step;
      el.classList.toggle('hidden', !isActive);
      el.toggleAttribute('hidden', !isActive);
      if (isActive) {
        el.setAttribute('aria-current', 'step');
      } else {
        el.removeAttribute('aria-current');
      }
    });

    const target = this.querySelector(`.tps-shutter-step-${step}`);
    if (target) {
      this.currentStep = step;
      this.scrollToTop();

      const heading = target.querySelector('[data-shutter-heading]');
      const headingText = heading ? heading.textContent.trim() : '';
      if (announce) {
        this.announce(`Step ${step} of ${totalSteps}${headingText ? `: ${headingText}` : ''}`);
      }

      this.focusElement(this.getStepFocusTarget(target));
    }
  }

  getStepFocusTarget(stepEl) {
    if (!stepEl) return null;

    return stepEl.querySelector('.tps-shutter-option-select')
      || stepEl.querySelector('.tps-shutter-option-group:not(.hidden) .tps-input:not([disabled])')
      || stepEl.querySelector('.restart-btn')
      || stepEl.querySelector('.tps-shutter-btn:not(.hidden), .tps-shutter-shop-btn, .tps-shutter-prev-btn');
  }

  focusElement(element) {
    if (!element) return;

    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
  }

  announce(message) {
    if (!this.liveRegion || !message) return;
    this.liveRegion.textContent = '';
    window.setTimeout(() => {
      this.liveRegion.textContent = message;
    }, 20);
  }

  scrollToTop() {
    const rect = this.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const top = rect.top + scrollTop - 100;
    window.scrollTo({
      top: top,
      behavior: this.reduceMotion ? 'auto' : 'smooth'
    });
  }

  showResult() {
    this.goToStep(5, { announce: false });

    const isValid = this.checkMeasurementsValidity();

    let resultIndex;
    if (this.selectedOption === '1') {
      resultIndex = isValid ? 1 : 2;
    } else {
      resultIndex = isValid ? 3 : 4;
    }

    this.querySelectorAll('.tps-result-value').forEach(el => {
      el.textContent = '';
    });

    if (isValid) {
      const widthKeys = ['width_top', 'width_middle', 'width_bottom'];
      const heightKeys = ['height_left', 'height_center', 'height_right'];

      const widthValues = widthKeys
        .map(key => this.inputs[key])
        .filter(value => typeof value === 'number');
      const heightValues = heightKeys
        .map(key => this.inputs[key])
        .filter(value => typeof value === 'number');

      if (widthValues.length && heightValues.length) {
        const minWidth = Math.min(...widthValues);
        const minHeight = Math.min(...heightValues);
        const format = (value) => {
          if (typeof value !== 'number' || !isFinite(value)) return '';

          const whole = Math.floor(value);
          const decimal = value - whole;

          // Precision 1/16
          const maxDenominator = 10;
          const numerator = Math.round(decimal * maxDenominator);

          if (numerator === 0) return whole.toString();
          if (numerator === maxDenominator) return (whole + 1).toString();

          const getGcd = (a, b) => b ? getGcd(b, a % b) : a;
          const gcd = getGcd(numerator, maxDenominator);
          const simplifiedNum = numerator / gcd;
          const simplifiedDen = maxDenominator / gcd;

          return whole === 0
            ? `<span class="tps-fraction"><sup>${simplifiedNum}</sup><span class="tps-fraction-slash">&frasl;</span><sub>${simplifiedDen}</sub></span>`
            : `${whole} <span class="tps-fraction"><sup>${simplifiedNum}</sup><span class="tps-fraction-slash">&frasl;</span><sub>${simplifiedDen}</sub></span>`;
        };
        const widthText = format(minWidth);
        const heightText = format(minHeight);
        const resultElForValue = this.querySelector(`.result-${resultIndex} .tps-result-value`);
        if (resultElForValue && widthText && heightText) {
          resultElForValue.innerHTML = `${widthText}"W × ${heightText}"L`;
        }
      }
    }

    this.querySelectorAll('.tps-shutter-result').forEach(el => el.classList.add('hidden'));

    const resultEl = this.querySelector(`.result-${resultIndex}`);
    if (resultEl) {
      resultEl.classList.remove('hidden');
      const resultText = resultEl.textContent.replace(/\s+/g, ' ').trim();
      if (resultText) this.announce(resultText);
    } else {
      this.querySelectorAll('.tps-shutter-result').forEach(el => el.classList.remove('hidden'));
    }
  }

  checkMeasurementsValidity() {
    const { diagonal_1, diagonal_2 } = this.inputs;
    if (typeof diagonal_1 !== 'number' || typeof diagonal_2 !== 'number') {
      return false;
    }
    const diff = Math.abs(diagonal_1 - diagonal_2);
    return diff <= 0.5;
  }

  reset() {
    this.currentStep = 1;
    this.selectedOption = null;
    this.inputs = {};

    this.querySelectorAll('.tps-shutter-inputs .tps-input').forEach(input => {
      input.value = '';
      input.disabled = false;
      this.clearInputError(input);
    });

    this.querySelectorAll('.tps-shutter-option-group').forEach((group) => {
      group.classList.remove('hidden');
    });

    this.querySelectorAll('.tps-shutter-btn.next-step').forEach(btn => {
      btn.classList.add('hidden');
    });

    this.goToStep(1);
  }
}

if (!customElements.get('tps-work-shutter')) {
  customElements.define('tps-work-shutter', TpsWorkShutter);
}
